/**
 * Logger utility for controlling when logs appear based on environment and debug flags
 * See logger.md in this directory for complete documentation and usage examples.
 */

// Configuration for the logger
interface ILoggerConfig {
    // Whether logging is enabled globally
    enabled: boolean;
    // Log only in development environment
    devOnly: boolean;
    // Use vConsole for mobile debugging
    useVConsole: boolean;
    // Filter string - only log messages containing this string
    filterString?: string;
}

// Default configuration
let config: ILoggerConfig = {
    enabled: true,
    devOnly: true,
    useVConsole: true,
};

// vConsole instance
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let vConsoleInstance: any = null;

/**
 * Dynamically imports and initializes vConsole
 *
 * Note: You need to install the vconsole package:
 * npm install vconsole@3.15.1 --save
 */
const initVConsole = async (): Promise<void> => {
    if (vConsoleInstance) return;

    try {
        // Use dynamic import for vConsole to avoid bundling it when not used
        const VConsole = (await import('vconsole')).default;
        vConsoleInstance = new VConsole();
        // eslint-disable-next-line no-console
        console.log('[LOGGER] vConsole initialized');
    } catch (error) {
        console.error('[LOGGER] Failed to initialize vConsole:', error);
        // If vConsole fails to load, disable it
        config.useVConsole = false;
    }
};

/**
 * Destroys the vConsole instance if it exists
 */
const destroyVConsole = (): void => {
    if (vConsoleInstance && typeof vConsoleInstance.destroy === 'function') {
        vConsoleInstance.destroy();
        vConsoleInstance = null;
        // eslint-disable-next-line no-console
        console.log('[LOGGER] vConsole destroyed');
    }
};

/**
 * Checks if logging should be performed based on current configuration
 */
const shouldLog = (): boolean => {
    if (!config.enabled) return false;
    if (config.devOnly && process.env.NODE_ENV !== 'development') return false;
    return true;
};

/**
 * Checks if a message should be logged based on the filter string
 * @param args The arguments to check against the filter
 */
const shouldLogMessage = (args: unknown[]): boolean => {
    if (!config.filterString) return true;

    // Convert all arguments to string and check if any contain the filter string
    const messageString = args.map((arg) => String(arg)).join(' ');

    return messageString.includes(config.filterString);
};

/**
 * Configure the logger
 * @param options Logger configuration options
 */
export const configureLogger = (options: Partial<ILoggerConfig>): void => {
    const prevUseVConsole = config.useVConsole;
    config = { ...config, ...options };

    // Handle vConsole initialization or destruction if the setting changed
    if (!prevUseVConsole && config.useVConsole) {
        initVConsole();
    } else if (prevUseVConsole && !config.useVConsole) {
        destroyVConsole();
    }
};

/**
 * Enable logging
 * @param devOnly Whether to log only in development environment (default: true)
 * @param useVConsole Whether to use vConsole for mobile debugging (default: false)
 * @param filterString Optional string filter - only log messages containing this string
 */
export const enableLogger = (devOnly = true, useVConsole = false, filterString?: string): void => {
    config.enabled = true;
    config.devOnly = devOnly;
    config.filterString = filterString;

    if (useVConsole !== config.useVConsole) {
        config.useVConsole = useVConsole;
        if (useVConsole) {
            initVConsole();
        } else {
            destroyVConsole();
        }
    }
};

/**
 * Disable logging
 */
export const disableLogger = (): void => {
    config.enabled = false;
    config.filterString = undefined;

    // Destroy vConsole when logging is disabled
    if (config.useVConsole) {
        destroyVConsole();
        config.useVConsole = false;
    }
};

/**
 * Enable vConsole for mobile debugging
 */
export const enableVConsole = (): void => {
    if (!config.useVConsole) {
        config.useVConsole = true;
        initVConsole();
    }
};

/**
 * Disable vConsole
 */
export const disableVConsole = (): void => {
    if (config.useVConsole) {
        config.useVConsole = false;
        destroyVConsole();
    }
};

/**
 * Set a filter string for logging - only messages containing this string will be logged
 * @param filterString The string to filter by, or undefined to remove filter
 */
export const setLogFilter = (filterString?: string): void => {
    config.filterString = filterString;
};

/**
 * Log information message (wrapper for console.log)
 * @param args Arguments to log
 */
export const log = (...args: unknown[]): void => {
    if (shouldLog() && shouldLogMessage(args)) {
        // eslint-disable-next-line no-console
        console.log('[INFO]', ...args);
    }
};

/**
 * Log error message (wrapper for console.error)
 * @param args Arguments to log
 */
export const error = (...args: unknown[]): void => {
    if (shouldLog() && shouldLogMessage(args)) {
        console.error('[ERROR]', ...args);
    }
};

/**
 * Log warning message (wrapper for console.warn)
 * @param args Arguments to log
 */
export const warn = (...args: unknown[]): void => {
    if (shouldLog() && shouldLogMessage(args)) {
        console.warn('[WARN]', ...args);
    }
};

/**
 * Log debug message (wrapper for console.debug)
 * @param args Arguments to log
 */
export const debug = (...args: unknown[]): void => {
    if (shouldLog() && shouldLogMessage(args)) {
        // eslint-disable-next-line no-console
        console.debug('[DEBUG]', ...args);
    }
};

// Create namespace for easier imports
export const Logger = {
    log,
    info: log,
    error,
    warn,
    debug,
    enable: enableLogger,
    disable: disableLogger,
    configure: configureLogger,
    enableVConsole,
    disableVConsole,
    setFilter: setLogFilter,
};

export default Logger;
